import {
  Clock,
  Component,
  Logger,
  Query,
  Scene,
  TileMap,
  TransformComponent,
  vec,
  Vector,
} from "excalibur";

export class TileComponent extends Component {
  public tilemap: TileMap | undefined;

  get pos() {
    if (!this.tilemap || !this.owner) throw new Error("Not on any map");
    const tc = this.owner.get(TransformComponent);
    if (!tc) throw new Error("No transform");
    const targetTile = this.tilemap.getTileByPoint(tc.pos);
    if (!targetTile)
      throw new Error(`Not found on map at ${tc.pos} for ${this.owner.name}`);

    return vec(targetTile.x, targetTile.y);
  }
}
export function tileKey(v: Vector): `${number},${number}`;
export function tileKey(x: number, y: number): `${number},${number}`;
export function tileKey(x: number | Vector, y?: number): `${number},${number}` {
  if (typeof x == "number") return `${x},${y ?? 0}`;
  return `${x.x},${x.y}`;
}

export class TilePositionManager {
  query: Query<typeof TileComponent>;
  clock: Clock;
  private _logger = Logger.getInstance();
  private _posCache: Map<`${number},${number}`, TileComponent[]> = new Map();
  private _lastCacheUpdateAt = 0;

  get cols() {
    return this.tilemap.width / this.tilemap.tileWidth;
  }

  get rows() {
    return this.tilemap.height / this.tilemap.tileHeight;
  }

  get width() {
    return this.tilemap.width;
  }

  get height() {
    return this.tilemap.height;
  }

  at(x: number, y: number): TileComponent[] {
    const n = this.clock.now();
    if (n > this._lastCacheUpdateAt) this.refreshPosCache();
    return this._posCache.get(tileKey(x, y)) ?? [];
  }

  refreshPosCache() {
    this._posCache.clear();
    for (const c of this.query.entities) {
      const tc = c.get(TileComponent);
      const pos = tileKey(tc.pos);
      const items = this._posCache.get(pos) ?? [];
      items.push(tc);
      this._posCache.set(pos, items);
    }
  }

  constructor(
    public scene: Scene,
    public tilemap: TileMap,
  ) {
    this.query = this.scene.world.query([TileComponent]);
    this.clock = this.scene.engine.clock;

    this.query.entityAdded$.subscribe((e) => {
      const tileComponent = e.get(TileComponent);
      tileComponent.tilemap = tilemap;
      this._logger.info("entity Addded", e.name, tileComponent.pos);
      this._lastCacheUpdateAt = 0; //reload cache;
    });

    this.query.entityRemoved$.subscribe((e) => {
      const tileComponent = e.get(TileComponent);
      tileComponent.tilemap = undefined;
      this._lastCacheUpdateAt = 0; //reload cache;
    });
  }

  getNeighborsAt(pos: Vector) {
    return neighbors(this, pos.x, pos.y);
  }
}

export function neighbors(
  tileManager: TilePositionManager,
  x: number,
  y: number,
): TileComponent[] {
  return [
    ...tileManager.at(x - 1, y),
    ...tileManager.at(x, y - 1),
    ...tileManager.at(x + 1, y),
    ...tileManager.at(x, y + 1),
  ];
}
